/**
 * InvestorAccordion — Gruppen-Ansicht für /investoren.
 *
 * Pattern-konsistent mit DataroomAccordion: Gold-Klammer links bei
 * ausgeklappter Sektion, Navy-Tint im Header, klare Trennung zwischen Gruppen.
 *
 * Gruppieren nach: phase | tier | sweet_spot | betreuer (per Dropdown).
 * Re-uses InvestorTable für die Detail-Ansicht pro Gruppe — alle bestehenden
 * Aktionen (Inline-Edit, Drawer, Bulk-Select) bleiben funktional.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Users, Search, X, Flame, Snowflake,
  ShieldAlert, Beaker, Sparkles, Wallet, UserCog,
} from "lucide-react";
import { listInvestorsGrouped } from "../../lib/api";
import InvestorTable from "./InvestorTable";

// Sicherheits-Trennzeichen: Color → Tailwind-Klassen (statische Strings — Tailwind
// JIT braucht das so, sonst werden Klassen wegoptimiert).
const COLOR_BADGE = {
  navy:    "bg-navy/8 text-navy border-navy/20",
  gold:    "bg-gold/10 text-[#8B6914] border-gold/40",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  sky:     "bg-sky-50 text-sky-800 border-sky-200",
  rose:    "bg-rose-50 text-rose-800 border-rose-200",
};

const COLOR_DOT = {
  navy: "bg-navy/50",
  gold: "bg-gold",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  rose: "bg-rose-500",
};

const GROUP_ICON = {
  // Sweet-Spot icons
  A_ls: Beaker,
  B_defensive: Wallet,
  C_value_add: Sparkles,
  Z_sonstige: Users,
  // Tier icons
  A_tier_a: Flame,
  B_tier_b: Flame,
  C_tier_c: Snowflake,
  Z_unranked: Users,
  X_bnp_exklusiv: ShieldAlert,
};

const GROUP_BY_OPTIONS = [
  { value: "phase",      label: "Phase" },
  { value: "tier",       label: "Tier (Hot / Warm / Cold)" },
  { value: "sweet_spot", label: "Sweet-Spot" },
  { value: "betreuer",   label: "Betreuer" },
];

const STORAGE_KEY_GROUPBY = "invacc.groupby";
const STORAGE_KEY_EXPANDED = "invacc.expanded";

function GroupBadge({ color, label, count }) {
  const cls = COLOR_BADGE[color] || COLOR_BADGE.navy;
  return (
    <span
      data-testid="invacc-group-badge"
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-sans border ${cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${COLOR_DOT[color] || COLOR_DOT.navy}`} />
      {label}
      <span className="tabular-nums opacity-70 ml-1">· {count}</span>
    </span>
  );
}

function GroupHeader({ group, expanded, onToggle, by }) {
  const Icon = GROUP_ICON[group.key] || Users;
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };
  const headerBg = expanded
    ? "bg-navy/[0.04] border-b border-gold/30"
    : "bg-white hover:bg-navy/[0.025]";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={handleKey}
      className={`w-full px-4 py-3 flex items-center gap-3 transition-colors text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-navy/20 ${headerBg}`}
      data-testid="invacc-group-header"
      aria-expanded={expanded}
    >
      <span className={`shrink-0 transition-colors ${expanded ? "text-gold" : "text-navy/55"}`}>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </span>
      <Icon size={18} className={`shrink-0 ${expanded ? "text-gold" : "text-navy/55"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display font-semibold text-navy text-base truncate">
            {group.label}
          </span>
          <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-sans tabular-nums ${COLOR_BADGE[group.color] || COLOR_BADGE.navy}`}>
            {group.count} {group.count === 1 ? "Investor" : "Investoren"}
          </span>
        </div>
        {by === "sweet_spot" && group.key === "A_ls" && (
          <div className="text-[11px] font-sans text-navy/55 mt-0.5">Spezialisten mit Life-Science-/Labor-Fokus</div>
        )}
        {by === "sweet_spot" && group.key === "B_defensive" && (
          <div className="text-[11px] font-sans text-navy/55 mt-0.5">Institutionelle mit hoher Erbbau-Affinität · WAULT-15+-Käufer</div>
        )}
        {by === "sweet_spot" && group.key === "C_value_add" && (
          <div className="text-[11px] font-sans text-navy/55 mt-0.5">Heiße Tier-1-3 Leads ohne LS-Spezialisierung</div>
        )}
        {by === "tier" && group.key === "X_bnp_exklusiv" && (
          <div className="text-[11px] font-sans text-rose-800 mt-0.5">BNP-Exklusiv-Sperre · wir dürfen nicht direkt ansprechen</div>
        )}
      </div>
    </div>
  );
}

export default function InvestorAccordion({
  onOpenDrawer,
  onSaved,
  refreshKey = 0,
}) {
  const [by, setBy] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY_GROUPBY) || "phase"; }
    catch { return "phase"; }
  });
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [tierMax, setTierMax] = useState(""); // "" | "3"
  const [ebrAff, setEbrAff] = useState("");
  const [lsAff, setLsAff] = useState("");
  const [onlyBnpExklusiv, setOnlyBnpExklusiv] = useState(false);
  const [excludeBnpExklusiv, setExcludeBnpExklusiv] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY_EXPANDED) || "[]")); }
    catch { return new Set(); }
  });

  // Debounce Search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Persist UI-State
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_GROUPBY, by); } catch {}
  }, [by]);
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_EXPANDED, JSON.stringify([...expanded])); } catch {}
  }, [expanded]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        by,
        search: searchDebounced || undefined,
        tier_max: tierMax || undefined,
        ebr_affinitaet: ebrAff || undefined,
        ls_affinitaet: lsAff || undefined,
        only_bnp_exklusiv: onlyBnpExklusiv || undefined,
        exclude_bnp_exklusiv: excludeBnpExklusiv || undefined,
      };
      const d = await listInvestorsGrouped(params);
      setData(d);
      // Bei aktiver Suche: alle Treffer-Gruppen automatisch ausklappen
      if (searchDebounced && d.groups) {
        const newExpanded = new Set();
        d.groups.forEach((g) => { if (g.count > 0) newExpanded.add(g.key); });
        setExpanded(newExpanded);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Investoren-Gruppen konnten nicht geladen werden");
      setData({ groups: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [by, searchDebounced, tierMax, ebrAff, lsAff, onlyBnpExklusiv, excludeBnpExklusiv]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    if (!data?.groups) return;
    const all = new Set(data.groups.filter((g) => g.count > 0).map((g) => g.key));
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set());

  const allExpanded = useMemo(() => {
    if (!data?.groups) return false;
    const nonEmpty = data.groups.filter((g) => g.count > 0);
    return nonEmpty.length > 0 && nonEmpty.every((g) => expanded.has(g.key));
  }, [data, expanded]);

  const clearFilters = () => {
    setSearch(""); setTierMax(""); setEbrAff(""); setLsAff("");
    setOnlyBnpExklusiv(false); setExcludeBnpExklusiv(false);
  };
  const hasFilters = !!(searchDebounced || tierMax || ebrAff || lsAff || onlyBnpExklusiv || excludeBnpExklusiv);

  return (
    <div data-testid="invacc-root" className="space-y-4">
      {/* Filterbar */}
      <section
        className="bg-white border border-navy/15 p-3 sm:p-4 space-y-3"
        data-testid="invacc-filterbar"
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              data-testid="invacc-search"
              type="text"
              placeholder="Firma oder Ansprechpartner suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pb-input--full pl-9 pr-8"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"
                aria-label="Suche leeren"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <select
            data-testid="invacc-groupby"
            value={by}
            onChange={(e) => setBy(e.target.value)}
            className="pb-input--full w-auto text-sm"
          >
            {GROUP_BY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>Gruppieren nach: {o.label}</option>
            ))}
          </select>
          <select
            data-testid="invacc-tier-max"
            value={tierMax}
            onChange={(e) => setTierMax(e.target.value)}
            className="pb-input--full w-auto text-sm"
          >
            <option value="">Alle Tier</option>
            <option value="1">nur Tier 1</option>
            <option value="2">Tier ≤ 2</option>
            <option value="3">Tier ≤ 3 (Heiße)</option>
          </select>
          <button
            onClick={allExpanded ? collapseAll : expandAll}
            className="pb-btn-ghost text-xs uppercase tracking-wider min-h-[36px]"
            data-testid="invacc-expand-all"
          >
            {allExpanded ? "Alle einklappen" : "Alle ausklappen"}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs font-sans">
          <select
            data-testid="invacc-ebr"
            value={ebrAff}
            onChange={(e) => setEbrAff(e.target.value)}
            className="pb-input--full w-auto text-xs"
          >
            <option value="">EBR-Affinität …</option>
            <option value="hoch">EBR hoch</option>
            <option value="mittel">EBR mittel</option>
            <option value="niedrig">EBR niedrig</option>
          </select>
          <select
            data-testid="invacc-ls"
            value={lsAff}
            onChange={(e) => setLsAff(e.target.value)}
            className="pb-input--full w-auto text-xs"
          >
            <option value="">LS-Affinität …</option>
            <option value="hoch">LS hoch</option>
            <option value="mittel">LS mittel</option>
            <option value="niedrig">LS niedrig</option>
          </select>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="invacc-only-bnp"
              checked={onlyBnpExklusiv}
              onChange={(e) => { setOnlyBnpExklusiv(e.target.checked); if (e.target.checked) setExcludeBnpExklusiv(false); }}
              className="accent-[#C4922A]"
            />
            <span className="text-navy/75">Nur BNP-Exklusiv</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="invacc-exclude-bnp"
              checked={excludeBnpExklusiv}
              onChange={(e) => { setExcludeBnpExklusiv(e.target.checked); if (e.target.checked) setOnlyBnpExklusiv(false); }}
              className="accent-[#C4922A]"
            />
            <span className="text-navy/75">Ohne BNP-Exklusiv</span>
          </label>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="ml-auto text-xs uppercase tracking-widish text-navy hover:text-gold inline-flex items-center gap-1 font-sans"
              data-testid="invacc-clear-filters"
            >
              <X size={12} /> zurücksetzen
            </button>
          )}
        </div>
      </section>

      {/* Summary-Strip */}
      {data && (
        <div className="flex items-center justify-between text-xs font-sans text-navy/65 px-1">
          <span data-testid="invacc-summary">
            <strong className="text-navy text-sm tabular-nums">{data.total}</strong>{" "}
            {data.total === 1 ? "Investor" : "Investoren"} ·{" "}
            <strong className="tabular-nums">{data.groups?.length || 0}</strong> Gruppen
            {by === "sweet_spot" && " (nach Sweet-Spot)"}
            {by === "tier" && " (nach Tier)"}
            {by === "phase" && " (nach Pipeline-Phase)"}
            {by === "betreuer" && " (nach Betreuer)"}
          </span>
          {loading && (
            <span className="text-navy/45 uppercase tracking-widish text-[10px]">lädt…</span>
          )}
        </div>
      )}

      {/* Gruppen-Akkordion */}
      <ul className="space-y-3" data-testid="invacc-list">
        {data?.groups?.map((group) => {
          const isOpen = expanded.has(group.key);
          return (
            <li
              key={group.key}
              data-expanded={isOpen}
              data-testid="invacc-section"
              className={
                "bg-white transition-all " +
                (isOpen
                  ? "border border-navy/15 border-l-[4px] border-l-gold"
                  : "border border-navy/12 border-l-[4px] border-l-transparent hover:border-l-gold/40")
              }
            >
              <GroupHeader
                group={group}
                expanded={isOpen}
                onToggle={() => toggle(group.key)}
                by={by}
              />
              {isOpen && (
                <div className="bg-navy/[0.015] pl-2 sm:pl-4 pr-1 sm:pr-2 py-2">
                  {group.count === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-navy/55 font-sans">
                      Keine Investoren in dieser Gruppe.
                    </div>
                  ) : (
                    <InvestorTable
                      items={group.items}
                      loading={false}
                      showTierColumn={true}
                      selectable={false}
                      onOpen={(id) => onOpenDrawer?.(id)}
                      onSaved={(updated) => {
                        // Optimistisch im lokalen Group-Datenbestand updaten,
                        // damit Inline-Edits nicht den ganzen Akkordion zurücksetzen.
                        setData((prev) => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            groups: prev.groups.map((g) =>
                              g.key !== group.key ? g : {
                                ...g,
                                items: g.items.map((x) => x.id === updated.id ? updated : x),
                              }
                            ),
                          };
                        });
                        onSaved?.(updated);
                      }}
                      testid={`invacc-table-${group.key}`}
                    />
                  )}
                  <div className="h-2 border-t border-gold/20 mt-1" aria-hidden="true" />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {!loading && data?.groups?.length === 0 && (
        <div className="bg-white border border-navy/15 px-6 py-10 text-center font-sans">
          <UserCog size={28} className="text-navy/30 mx-auto mb-3" />
          <div className="text-navy/65 text-sm">Keine Investoren mit aktuellen Filtern gefunden.</div>
          {hasFilters && (
            <button onClick={clearFilters} className="mt-3 text-xs uppercase tracking-widish text-gold hover:text-navy">
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Re-Export der Badge-Komponente, damit Container sie wiederverwenden können.
export { GroupBadge };
